import { useState, useMemo } from 'react'
import { Loader2, AlertCircle } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import * as SelectPrimitive from "@radix-ui/react-select"
import { FlashCard } from "@/components/shared/tattoo/FlashCard"
import type { PortfolioItem, Artist } from "@/types/models"
import { doc, updateDoc, getDoc } from "firebase/firestore"
import { db } from "@/lib/firebase"
import { auth } from "@/lib/firebase"

interface DiscountSheetProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  flash: PortfolioItem
  artist?: Artist
  onDiscountChanged?: () => void
}

export interface DiscountData {
  discountPercentage: number
  duration: '7' | '14' | '30' | '0'
}

export function DiscountSheet({ isOpen, onOpenChange, flash, artist, onDiscountChanged }: DiscountSheetProps) {
  const [discountPercentage, setDiscountPercentage] = useState<string>('')
  const [duration, setDuration] = useState<string>('0')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string>('')
  
  // Check if there's an existing active discount
  const hasExistingDiscount = !!flash.discount

  // Calculate the new price based on the discount percentage
  const discountedPrice = useMemo(() => {
    if (!discountPercentage || !flash.minPrice) return null
    
    const percentage = parseFloat(discountPercentage)
    if (isNaN(percentage) || percentage < 0 || percentage > 100) return null
    
    const originalPrice = parseFloat(flash.minPrice)
    const discountAmount = originalPrice * (percentage / 100)
    const newPrice = originalPrice - discountAmount
    
    return {
      percentage,
      originalPrice,
      newPrice: Math.round(Math.max(0, newPrice)),
      discountAmount: Math.round(discountAmount * 100) / 100
    }
  }, [discountPercentage, flash.minPrice])

  // Create a preview item with the discount applied
  const previewFlash: PortfolioItem = useMemo(() => {
    if (!discountedPrice) return flash

    const durationDays = duration === '0' ? 365 * 100 : parseInt(duration) // Use a large number for "Forever"
    const now = new Date()
    const endsAt = new Date(now.getTime() + durationDays * 24 * 60 * 60 * 1000)

    return {
      ...flash,
      discount: {
        procentualAccount: discountedPrice.percentage,
        newPrice: discountedPrice.newPrice,
        oldPrice: discountedPrice.originalPrice,
        discountStartsAt: now,
        discountEndsAt: duration === '0' ? new Date(9999, 11, 31) : endsAt,
      }
    }
  }, [flash, discountedPrice, duration])

  const handleClose = () => {
    if (!isSaving) {
      // Reset state when closing
      setDiscountPercentage('')
      setDuration('0')
      setError('')
      onOpenChange(false)
    }
  }

  const handleSave = async () => {
    setError('')
    
    // Validate input
    if (!discountPercentage) {
      setError('Please enter a discount percentage')
      return
    }

    const percentage = parseFloat(discountPercentage)
    if (isNaN(percentage) || percentage < 0 || percentage > 100) {
      setError('Discount must be between 0 and 100%')
      return
    }

    if (!flash.minPrice) {
      setError('Flash does not have a base price set')
      return
    }

    setIsSaving(true)
    try {
      const currentUser = auth.currentUser
      if (!currentUser) {
        throw new Error('You must be logged in to set discounts')
      }

      // Verify user is the artist who owns this flash
      // TODO: Remove this permission bypass after testing
      const ALLOW_ANY_DISCOUNT_FOR_TESTING = true
      
      if (!ALLOW_ANY_DISCOUNT_FOR_TESTING) {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid))
        if (!userDoc.exists()) {
          throw new Error('User document not found')
        }

        const userData = userDoc.data()
        if (userData?.artistId !== flash.artistId) {
          throw new Error('Permission denied: You can only set discounts on your own flashes')
        }
      }

      // Calculate discount info
      const originalPrice = parseFloat(flash.minPrice)
      const discountAmount = originalPrice * (percentage / 100)
      const newPrice = Math.round(originalPrice - discountAmount)

      // Calculate end date
      const now = new Date()
      const endsAt = duration === '0' 
        ? new Date(9999, 11, 31) // Forever
        : new Date(now.getTime() + parseInt(duration) * 24 * 60 * 60 * 1000)

      // Update Firestore - try main collection first
      let flashRef = doc(db, 'flashes', flash.id)
      let flashSnap = await getDoc(flashRef)
      
      // If not found in main collection, try archived
      if (!flashSnap.exists()) {
        console.log('[DiscountSheet] Flash not found in main collection, trying archived...')
        flashRef = doc(db, 'flashesArchived', flash.id)
        flashSnap = await getDoc(flashRef)
      }
      
      if (!flashSnap.exists()) {
        throw new Error('Flash not found')
      }
      
      await updateDoc(flashRef, {
        discount: {
          procentualAccount: percentage,
          newPrice,
          oldPrice: originalPrice,
          discountStartsAt: now,
          discountEndsAt: endsAt,
        }
      })

      // Reset and close
      setDiscountPercentage('')
      setDuration('0')
      onDiscountChanged?.()
      onOpenChange(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save discount'
      setError(message)
      console.error('[DiscountSheet] Error saving discount:', err)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    handleClose()
  }

  const handleEndDiscount = async () => {
    setError('')
    setIsSaving(true)
    try {
      const currentUser = auth.currentUser
      if (!currentUser) {
        throw new Error('You must be logged in to end discounts')
      }

      // Update Firestore - try main collection first
      let flashRef = doc(db, 'flashes', flash.id)
      let flashSnap = await getDoc(flashRef)
      
      // If not found in main collection, try archived
      if (!flashSnap.exists()) {
        console.log('[DiscountSheet] Flash not found in main collection, trying archived...')
        flashRef = doc(db, 'flashesArchived', flash.id)
        flashSnap = await getDoc(flashRef)
      }
      
      if (!flashSnap.exists()) {
        throw new Error('Flash not found')
      }
      
      // Remove the discount field
      await updateDoc(flashRef, {
        discount: null
      })

      console.log('[DiscountSheet] Discount ended successfully')
      onDiscountChanged?.()
      onOpenChange(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to end discount'
      setError(message)
      console.error('[DiscountSheet] Error ending discount:', err)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent 
        side="bottom" 
        className="h-auto flex flex-col p-6 max-w-[calc(700px+1.5rem)] mx-auto left-0 right-0 rounded-t-2xl"
        hideCloseButton
      >
        <SheetHeader className="pb-4">
          <SheetTitle className="text-xl font-semibold">
            Apply Discount
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-6">
          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Flash Card Preview - Display with live discount preview */}
          <div className="max-w-xs mx-auto">
            <FlashCard
              item={previewFlash}
              artist={artist}
              editMode={false}
              preloaded={true}
            />
          </div>

          {/* Discount Form */}
          <div className="space-y-4">
            {/* Percentage and Duration Row */}
            <div className="grid grid-cols-[1fr_1fr] gap-3">
              {/* Discount Percentage Selection */}
              <div className="space-y-2">
                <Label htmlFor="discount-percentage" className="text-xs font-medium">
                  Discount %
                </Label>
                <Select value={discountPercentage} onValueChange={setDiscountPercentage} disabled={isSaving}>
                  <SelectTrigger id="discount-percentage" className="text-sm">
                    <SelectValue placeholder="Select discount" />
                  </SelectTrigger>
                  <SelectPrimitive.Portal>
                    <SelectContent className="z-[120] max-h-[200px]">
                      {Array.from({ length: 19 }, (_, i) => (i + 1) * 5).map((percentage) => (
                        <SelectItem key={percentage} value={percentage.toString()}>
                          {percentage}%
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </SelectPrimitive.Portal>
                </Select>
              </div>

              {/* Duration Selection */}
              <div className="space-y-2">
                <Label htmlFor="duration" className="text-xs font-medium">
                  Duration
                </Label>
                <Select value={duration} onValueChange={setDuration} disabled={isSaving}>
                  <SelectTrigger id="duration" className="text-sm">
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectPrimitive.Portal>
                    <SelectContent className="z-[120]">
                      <SelectItem value="7">1 Week</SelectItem>
                      <SelectItem value="14">2 Weeks</SelectItem>
                      <SelectItem value="30">1 Month</SelectItem>
                      <SelectItem value="0">Forever</SelectItem>
                    </SelectContent>
                  </SelectPrimitive.Portal>
                </Select>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3 pt-2">
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 h-14 text-base"
                onClick={handleCancel}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 h-14 text-base"
                onClick={handleSave}
                disabled={isSaving || !discountPercentage || !discountedPrice}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Discount'
                )}
              </Button>
            </div>
            
            {/* End Discount Button - Show but disable if no existing discount */}
            <Button
              className="w-full h-14 text-base bg-red-500 hover:bg-red-600 text-white"
              onClick={handleEndDiscount}
              disabled={isSaving || !hasExistingDiscount}
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Ending...
                </>
              ) : (
                'End Discount'
              )}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

